import {TranslateService} from '@ngx-translate/core';
import {Component, forwardRef, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {ModeEnum, ProductTypeEnum, Remuneration, UnitEnum} from '@tj/tj-dmp-lib-api';
import {RemunerationTransactionType} from '../remuneration-configuration';

export enum GeoFilterModeEnum {
    NONE = 'NONE',
    CITY = 'CITY',
    AIRPORT = 'AIRPORT',
    COUNTRY = 'COUNTRY'
}

@Component({
    selector: 'dmp-product-remuneration-selector',
    templateUrl: './product-remuneration-selector.component.html',
    styleUrls: ['./product-remuneration-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ProductRemunerationSelectorComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ProductRemunerationSelectorComponent),
            multi: true,
        }
    ],
    standalone: false
})
export class ProductRemunerationSelectorComponent implements ControlValueAccessor, Validator, OnChanges {
    get locale(): string { return this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }

    constructor(private readonly translate: TranslateService) {}

    public geoFilterModes = [
        {label: '...', value: GeoFilterModeEnum.NONE},
        {label: this.translate.instant('invoicing.remuneration.byCity'), value: GeoFilterModeEnum.CITY},
        {label: this.translate.instant('invoicing.remuneration.byAirport'), value: GeoFilterModeEnum.AIRPORT},
        {label: this.translate.instant('invoicing.remuneration.byCountry'), value: GeoFilterModeEnum.COUNTRY}
    ];

    protected readonly GeoFilterModeEnum = GeoFilterModeEnum;
    protected readonly ProductTypeEnum = ProductTypeEnum;

    @Input() datastore: string | null = null;
    @Input() paymentsMethodOptions: Array<{ label: string; value: string; disabled?: boolean }> = [];
    @Input() readOnly = false;
    @Input() submitted = false;
    @Input() showProvider = true;
    @Input() showProduct = true;
    @Input() showPaymentMethod = true;
    @Input() showGeoFilters = true;
    @Input() showOptionalPeriod = false;
    @Input() transactionType: RemunerationTransactionType = 'command';

    remunerationForm!: FormGroup;
    get remunerationModes() {
        const fixed = {label: this.translate.instant('invoicing.remuneration.fixed'), value: ModeEnum.FIXED};
        if (this.transactionType === 'lead') { return [fixed]; }
        return [
            {label: this.translate.instant('invoicing.remuneration.turnoverPercentage'), value: ModeEnum.PERCENTAGE},
            fixed
        ];
    }
    get units() {
        return [
            {label: this.transactionType === 'lead' ? this.translate.instant('invoicing.remuneration.request') : this.translate.instant('invoicing.remuneration.record'), value: UnitEnum.FOLDER},
            {label: this.translate.instant('invoicing.remuneration.passenger'), value: UnitEnum.PASSENGER, disabled: this.transactionType === 'lead'}
        ];
    }

    protected readonly ModeEnum = ModeEnum;

    public static initFormGroup(
        remuneration?: Remuneration,
        defaultProvider?: string,
        defaultPaymentMethod?: string
    ) {
        const remunerationWithFilters = remuneration as (Remuneration & {
            provider?: string;
            paymentMethod?: string;
            flightDepartureCity?: string;
            flightDepartureAirport?: string;
            flightArrivalCity?: string;
            flightArrivalAirport?: string;
        }) | undefined;
        const getFlightGeoMode = (cities?: string[], airports?: string[], city?: string, airport?: string, countries?: string[]): GeoFilterModeEnum => {
            if ((cities?.length || 0) > 0 || !!city) {
                return GeoFilterModeEnum.CITY;
            } else if ((airports?.length || 0) > 0 || !!airport) {
                return GeoFilterModeEnum.AIRPORT;
            } else if (countries?.length) {
                return GeoFilterModeEnum.COUNTRY;
            } else {
                return GeoFilterModeEnum.NONE;
            }
        };
        const normalizeValues = (values?: string[], legacyValue?: string): string[] => {
            if (Array.isArray(values)) {
                return [...values];
            }
            return legacyValue ? [legacyValue] : [];
        };
        const isLead = remuneration?.typeTransaction === 'lead';
        return {
            productType: [remuneration?.productType || ProductTypeEnum.FLIGHT, Validators.required],
            mode: [isLead ? ModeEnum.FIXED : remuneration?.mode || ModeEnum.PERCENTAGE, Validators.required],
            percentage: [isLead ? null : remuneration?.percentage ?? null],
            fixedAmount: [remuneration?.fixedAmount ?? null],
            unit: [isLead ? UnitEnum.FOLDER : remuneration?.unit || null],
            startDate: [remuneration?.startDate ? new Date(remuneration.startDate) : null],
            endDate: [remuneration?.endDate ? new Date(remuneration.endDate) : null],
            defaultRemuneration: [remuneration?.defaultRemuneration ?? false],
            flightDepartureGeoMode: [getFlightGeoMode(
                remuneration?.flightDepartureCities,
                remuneration?.flightDepartureAirports,
                remunerationWithFilters?.flightDepartureCity,
                remunerationWithFilters?.flightDepartureAirport,
                remuneration?.flightDepartureCountries
            )],
            flightDepartureCountries: [normalizeValues(remuneration?.flightDepartureCountries)],
            flightDepartureCities: [normalizeValues(
                remuneration?.flightDepartureCities,
                remunerationWithFilters?.flightDepartureCity
            )],
            flightDepartureAirports: [normalizeValues(
                remuneration?.flightDepartureAirports,
                remunerationWithFilters?.flightDepartureAirport
            )],
            flightArrivalGeoMode: [getFlightGeoMode(
                remuneration?.flightArrivalCities,
                remuneration?.flightArrivalAirports,
                remunerationWithFilters?.flightArrivalCity,
                remunerationWithFilters?.flightArrivalAirport,
                remuneration?.flightArrivalCountries
            )],
            flightArrivalCountries: [normalizeValues(remuneration?.flightArrivalCountries)],
            flightArrivalCities: [normalizeValues(
                remuneration?.flightArrivalCities,
                remunerationWithFilters?.flightArrivalCity
            )],
            flightArrivalAirports: [normalizeValues(
                remuneration?.flightArrivalAirports,
                remunerationWithFilters?.flightArrivalAirport
            )],
            flightMarketingCarriers: [normalizeValues(remuneration?.flightMarketingCarriers)],
            provider: [remunerationWithFilters?.provider ?? defaultProvider ?? null],
            paymentMethod: [remunerationWithFilters?.paymentMethod ?? defaultPaymentMethod ?? null]
        };
    }

    private onChange: (value: any) => void = () => {
    }
    private onTouched: () => void = () => {
    }
    private onValidatorChange: () => void = () => {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.readOnly || changes.transactionType) {
            this.applyReadOnlyState();
        }
    }

    private init(formGroup: FormGroup) {
        this.remunerationForm = formGroup;
        this.remunerationForm.addValidators(this.companyGeoOverrideValidator());
        this.remunerationForm.addValidators(this.periodOrderValidator());

        this.remunerationForm.get('percentage')?.setValidators([this.requiredByModeValidator(ModeEnum.PERCENTAGE)]);
        this.remunerationForm.get('fixedAmount')?.setValidators([this.requiredByModeValidator(ModeEnum.FIXED)]);
        this.remunerationForm.get('unit')?.setValidators([this.requiredByModeValidator(ModeEnum.FIXED)]);
        this.remunerationForm.get('provider')?.setValidators([Validators.required]);

        this.remunerationForm.get('mode')?.valueChanges.subscribe(() => {
            this.remunerationForm.get('percentage')?.updateValueAndValidity({onlySelf: true});
            this.remunerationForm.get('fixedAmount')?.updateValueAndValidity({onlySelf: true});
            this.remunerationForm.get('unit')?.updateValueAndValidity({onlySelf: true});
            this.onValidatorChange();
        });

        this.remunerationForm.get('mode')?.updateValueAndValidity({onlySelf: true, emitEvent: true});

        this.remunerationForm.get('provider')?.updateValueAndValidity({onlySelf: true});

        const departureCountries = this.remunerationForm.get('flightDepartureCountries');
        departureCountries?.setValidators(control =>
            control.parent?.get('flightDepartureGeoMode')?.value === GeoFilterModeEnum.COUNTRY
                ? Validators.required(control) : null);
        departureCountries?.updateValueAndValidity({onlySelf: true});
        this.remunerationForm.get('flightDepartureGeoMode')?.valueChanges.subscribe((geoMode: GeoFilterModeEnum) => {
            this.updateGeoMode('Departure', geoMode);
        });

        const arrivalCountries = this.remunerationForm.get('flightArrivalCountries');
        arrivalCountries?.setValidators(control =>
            control.parent?.get('flightArrivalGeoMode')?.value === GeoFilterModeEnum.COUNTRY
                ? Validators.required(control) : null);
        arrivalCountries?.updateValueAndValidity({onlySelf: true});
        this.remunerationForm.get('flightArrivalGeoMode')?.valueChanges.subscribe((geoMode: GeoFilterModeEnum) => {
            this.updateGeoMode('Arrival', geoMode);
        });

        this.remunerationForm.valueChanges.subscribe(() => {
            this.onChange(this.remunerationForm);
        });

        this.remunerationForm.statusChanges.subscribe(() => {
            this.onValidatorChange();
        });

        this.applyReadOnlyState();
        this.onValidatorChange();
    }

    private updateGeoMode(side: 'Departure' | 'Arrival', geoMode: GeoFilterModeEnum): void {
        for (const [mode, suffix] of [
            [GeoFilterModeEnum.CITY, 'Cities'],
            [GeoFilterModeEnum.AIRPORT, 'Airports'],
            [GeoFilterModeEnum.COUNTRY, 'Countries']
        ]) {
            const control = this.remunerationForm.get(`flight${side}${suffix}`);
            if (geoMode !== mode) {
                control?.setValue([]);
            } else {
                control?.updateValueAndValidity();
            }
        }
    }

    writeValue(value: any): void {
        if (value) {
            this.init(value);
            // this.remunerationForm.setValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    validate(_: AbstractControl): ValidationErrors | null {
        if (!this.remunerationForm || this.remunerationForm.disabled) {
            return null;
        }
        return this.remunerationForm.valid ? null : {remunerationInvalid: true};
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    setTouched() {
        this.onTouched(); // Indique que le composant a été touché
    }

    setDisabledState(isDisabled: boolean): void {
        if (this.remunerationForm) {
            if (isDisabled) {
                this.remunerationForm.disable({emitEvent: false});
                this.onValidatorChange();
            } else {
                this.remunerationForm.enable({emitEvent: false});
                this.applyReadOnlyState();
            }
        }
    }

    private applyReadOnlyState(): void {
        if (!this.remunerationForm) {
            return;
        }
        const payment = this.remunerationForm.get('paymentMethod');
        if (this.transactionType === 'lead') {
            this.remunerationForm.patchValue({
                mode: ModeEnum.FIXED, percentage: null, unit: UnitEnum.FOLDER, paymentMethod: null
            }, {emitEvent: false});
            for (const name of ['percentage', 'fixedAmount', 'unit']) {
                this.remunerationForm.get(name)?.updateValueAndValidity({onlySelf: true, emitEvent: false});
            }
        }
        if (this.readOnly) {
            this.remunerationForm.disable({emitEvent: false});
            this.onValidatorChange();
            return;
        }
        this.remunerationForm.enable({emitEvent: false});
        if (this.transactionType === 'lead') {
            payment?.disable({emitEvent: false});
        }
        this.remunerationForm.updateValueAndValidity({emitEvent: false});
        this.onValidatorChange();
    }

    isControlInvalid(controlName: string): boolean {
        const control = this.remunerationForm?.get(controlName);
        return !!control && control.invalid && (this.submitted || control.touched || control.dirty);
    }

    private requiredByModeValidator(requiredMode: ModeEnum): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const modeControl = control.parent?.get('mode');
            const mode = modeControl?.value as ModeEnum | null;
            if (!mode || mode !== requiredMode) {
                return null;
            }
            if (this.isEmptyValue(control.value)) { return {requiredByMode: true}; }
            if (control === control.parent?.get('percentage') || control === control.parent?.get('fixedAmount')) {
                const value = Number(control.value);
                if (!Number.isFinite(value) || value < 0 || (requiredMode === ModeEnum.PERCENTAGE && value > 100)) {
                    return {invalidRemunerationValue: true};
                }
            }
            return null;
        };
    }

    get overrideCriterionMessage(): string {
        if (this.transactionType === 'lead') {
            return this.remunerationForm?.get('productType')?.value === ProductTypeEnum.FLIGHT
                ? this.translate.instant('invoicing.remuneration.overrideFlightLead')
                : this.translate.instant('invoicing.remuneration.overrideLead');
        }
        return this.remunerationForm?.get('productType')?.value === ProductTypeEnum.FLIGHT
            ? this.translate.instant('invoicing.remuneration.overrideFlightSale')
            : this.translate.instant('invoicing.remuneration.overrideSale');
    }

    private companyGeoOverrideValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const carriers = control.get('flightMarketingCarriers')?.value;
            const hasAirlineFilter = Array.isArray(carriers) && carriers.length > 0;
            if (control.get('defaultRemuneration')?.value === true) {
                return hasAirlineFilter ? {airlineFilterOnDefault: true} : null;
            }
            const hasGeoFilter = [
                'flightDepartureCities',
                'flightDepartureAirports',
                'flightDepartureCountries',
                'flightArrivalCities',
                'flightArrivalAirports',
                'flightArrivalCountries'
            ].some(controlName => {
                const value = control.get(controlName)?.value;
                return Array.isArray(value) && value.length > 0;
            });
            if (hasGeoFilter && control.get('productType')?.value !== ProductTypeEnum.FLIGHT) {
                return {geoFilterNotSupported: true};
            }
            if (hasAirlineFilter && control.get('productType')?.value !== ProductTypeEnum.FLIGHT) {
                return {airlineFilterNotSupported: true};
            }
            const hasPaymentMethod = this.transactionType === 'command'
                && !this.isEmptyValue(control.get('paymentMethod')?.value);
            const hasPeriod = !this.isEmptyValue(control.get('startDate')?.value)
                || !this.isEmptyValue(control.get('endDate')?.value);
            return hasGeoFilter || hasAirlineFilter || hasPaymentMethod || hasPeriod ? null : {overrideCriterionRequired: true};
        };
    }

    private periodOrderValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const start = control.get('startDate')?.value;
            const end = control.get('endDate')?.value;
            const startDate = start ? new Date(start) : null;
            const endDate = end ? new Date(end) : null;
            if ((startDate && Number.isNaN(startDate.getTime())) || (endDate && Number.isNaN(endDate.getTime()))) {
                return {invalidPeriod: true};
            }
            if (!startDate || !endDate) { return null; }
            const day = (date: Date) => date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
            return day(endDate) < day(startDate) ? {invalidPeriod: true} : null;
        };
    }

    private isEmptyValue(value: unknown): boolean {
        return value === null || value === undefined || value === '';
    }
}
